import {
  Avatar,
  CheckIcon,
  Group,
  Select,
  type SelectProps,
  Text,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import { IconChevronDown } from "@tabler/icons-react";
import type { ReactNode } from "react";
import React from "react";
import { useTranslation } from "react-i18next";

// `label` is formatted by the caller; the names only feed the avatar initials
export type SelectUser = {
  id: string;
  label: string;
  firstName: string;
  lastName: string;
};

const getFullName = (user: SelectUser) => `${user.firstName} ${user.lastName}`;

export type UserSelectProps = {
  onChange: (userId: string | null) => void;
  data: SelectUser[];
  label: ReactNode;
  placeholder?: string;
  mb?: string;
};

const useStyles = createStyles(theme => ({
  input: {
    "&:not(:placeholder-shown)": {
      fontWeight: theme.fontWeights.medium,
    },
  },
}));

export const UserSelect = ({
  onChange,
  data,
  label,
  placeholder,
  ...rest
}: UserSelectProps) => {
  const { t } = useTranslation("forms");
  const { classes } = useStyles();

  const [selected, setSelected] = React.useState<SelectUser | null>(null);

  const renderSelectOption: SelectProps["renderOption"] = ({
    option,
    checked,
  }) => {
    const user = data.find(u => u.id === option.value);

    return (
      <Group flex="1" gap="xs">
        {checked && <CheckIcon size={14} />}
        <Avatar
          size="sm"
          name={user ? getFullName(user) : option.label}
          color="indigo"
        />
        <Text variant="small" size="sm" fw="500">
          {option.label}
        </Text>
      </Group>
    );
  };

  return (
    <Select
      radius="xl"
      size="md"
      {...rest}
      classNames={{ input: classes.input }}
      label={label}
      placeholder={placeholder ?? t("components.userSelect.placeholder")}
      data={data.map(user => ({
        value: user.id,
        label: user.label,
      }))}
      clearable
      clearSectionMode="clear"
      renderOption={renderSelectOption}
      onChange={(userId: string | null) => {
        setSelected(data.find(u => u.id === userId)!);
        onChange(userId);
      }}
      leftSection={
        selected && (
          <Avatar color="indigo" size="sm" name={getFullName(selected)} />
        )
      }
      rightSection={<IconChevronDown />}
    />
  );
};
